'use client';

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { badgeService } from '@/lib/badgeService';
import type { BadgeCounts } from '@/lib/badgeService';
import { loadCachedBadgeCounts, saveCachedBadgeCounts } from '@/lib/badgeCache';
import { useAuth } from '@/hooks/useAuth';
import { useTaskbarBadge } from '@/hooks/useTaskbarBadge';
import { Capacitor } from '@capacitor/core';
import AppBadge from '@/plugins/AppBadge';
import { maintenanceState } from '@/lib/maintenanceState';
import { isAppShellRoute } from '@/lib/platformUtils';
import { isStandalonePwa } from '@/lib/pwaAppIdentity';
import { getHybridRuntimeWindow } from '@/lib/runtimeWindow';
import { useSession } from '@/components/SessionProvider';
import { isPortalSessionLocked } from '@/lib/sessionLockState';

interface BadgeContextType {
  badgeCounts: BadgeCounts;
  refreshBadgeCounts: () => Promise<void>;
  setMissedCallsCount: (count: number) => void;
  loading: boolean;
}

const BadgeContext = createContext<BadgeContextType | undefined>(undefined);

const ZERO_COUNTS: BadgeCounts = {
  totalUnreadCount: 0,
  messagesUnreadCount: 0,
  emailUnreadCount: 0,
  calendarUnreadCount: 0,
  workspaceUnreadCount: 0,
  tasksDueTodayCount: 0,
  pendingMentionsCount: 0,
  pendingApprovalsCount: 0,
  missedCallsCount: 0,
  activeTaskCount: 0,
  clockStatus: 'none',
};

export function BadgeProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const session = useSession();
  const isSessionLocked = session.sessionState.isLocked || isPortalSessionLocked();
  const [badgeCounts, setBadgeCounts] = useState<BadgeCounts>(ZERO_COUNTS);
  const [loading, setLoading] = useState(false);

  // Ref mirror of badgeCounts so refreshBadgeCounts can read the current
  // activeTaskCount without adding it to the dependency array (which would
  // cause the polling effect to reset on every active count change).
  const badgeCountsRef = useRef(badgeCounts);
  useEffect(() => { badgeCountsRef.current = badgeCounts; }, [badgeCounts]);

  // ── Load cached badge counts from IndexedDB on mount ────────────────────
  // This makes badges appear instantly on cold start, before the first API
  // refresh completes. The API refresh will overwrite with fresh values shortly.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let cancelled = false;
    void (async () => {
      const cached = await loadCachedBadgeCounts();
      if (!cancelled && cached) {
        setBadgeCounts(prev => {
          // Only update if any value differs from the current (zero) state
          const hasChanged = (Object.keys(cached) as Array<keyof BadgeCounts>).some(
            key => prev[key] !== cached[key]
          );
          return hasChanged ? cached : prev;
        });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Persist badge counts to IndexedDB whenever they change ──────────────
  useEffect(() => {
    void saveCachedBadgeCounts(badgeCounts);
  }, [badgeCounts]);

  // ── Listen for active task count updates from SmartTaskShell ────────────
  // SmartTaskShell computes the active task count client-side (from workflow
  // state + work items) and dispatches this event. We merge it into the
  // centralized badge state so all consumers (footer, dashboard, etc.) see
  // the same value without each needing their own listener.
  // We take the max of the event count and the current value to avoid
  // flickering down to 0 during async loading races — the badge-counts API
  // (queried from field_worker_workflow_state) is the source of truth, and
  // the client-side event provides real-time granularity.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handler = (e: Event) => {
      const count = (e as CustomEvent<{ count?: number }>).detail?.count ?? 0;
      setBadgeCounts(prev => {
        const next = Math.max(count, prev.activeTaskCount);
        if (prev.activeTaskCount === next) return prev;
        return { ...prev, activeTaskCount: next };
      });
    };
    window.addEventListener('smarttask-active-count-changed', handler as EventListener);
    return () => window.removeEventListener('smarttask-active-count-changed', handler as EventListener);
  }, []);

  const refreshBadgeCounts = useCallback(async () => {
    // Skip if in maintenance mode — DB is down, no point hitting APIs
    if (maintenanceState.isInMaintenance) {
      return;
    }

    if (isSessionLocked) {
      return;
    }

    // Skip if offline - don't attempt network requests
    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return;
    }

    // Public/marketing pages never render badges — skip the authed call so a
    // stale session doesn't produce a 401 (and a pointless poll) there.
    if (typeof window !== 'undefined' && !isAppShellRoute(window.location.pathname)) {
      return;
    }

    if (!isAuthenticated) {
      setBadgeCounts(prev => {
        // Only update if values actually changed (preserve activeTaskCount and
        // clockStatus which are managed client-side / from the API)
        const hasChanged = (Object.keys(ZERO_COUNTS) as Array<keyof BadgeCounts>).some(
          key => key !== 'activeTaskCount' && key !== 'clockStatus' && prev[key] !== ZERO_COUNTS[key]
        );
        return hasChanged ? { ...ZERO_COUNTS, activeTaskCount: prev.activeTaskCount, clockStatus: prev.clockStatus } : prev;
      });
      return;
    }

    setLoading(true);
    try {
      const counts = await badgeService.getBadgeCounts();
      // activeTaskCount now comes from the badge-counts API (queried from
      // field_worker_workflow_state). The API is the source of truth — when
      // it says 0, the count goes to 0 (the user completed/closed the job).
      // Client-side smarttask-active-count-changed events can only increase
      // the count (real-time granularity), never decrease it below the API
      // baseline. This prevents the 0 → N → 0 flicker during async loading
      // races while still allowing the count to reset when the workflow
      // actually transitions to idle.
      const merged = counts;
      // Only update state if values actually changed to prevent unnecessary re-renders
      setBadgeCounts(prev => {
        const hasChanged = (Object.keys(merged) as Array<keyof BadgeCounts>).some(
          key => prev[key] !== merged[key]
        );
        return hasChanged ? merged : prev;
      });
    } catch (error) {
      // Silently fail for badge counts - don't spam console
      console.debug('Badge counts unavailable:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, isSessionLocked]);

  // Simple polling-based badge updates (every 2 minutes, reduced from 30s to save battery on mobile)
  // OPTIMIZATION: Defer initial load on Capacitor to avoid blocking cold start
  // OPTIMIZATION: Only poll when document is visible to avoid wasting resources when app is backgrounded
  useEffect(() => {
    // Only run on client-side (prevents SSR fetch errors)
    if (typeof window === 'undefined') return;

    if (isAuthenticated && !isSessionLocked) {
      // On Capacitor, defer initial badge fetch to avoid blocking cold start
      // Use requestIdleCallback to wait for idle time, with fallback for iOS
      const isCapacitorNative = Capacitor.isNativePlatform();

      const doInitialLoad = () => {
        if (document.visibilityState === 'visible') {
          refreshBadgeCounts();
        }
      };

      const pollInterval = 120000; // 2 minutes (reduced from 30s)

      if (isCapacitorNative) {
        const runtimeWindow = getHybridRuntimeWindow();
        // Defer to idle time on mobile to improve cold start
        if (runtimeWindow?.requestIdleCallback) {
          const idleId = runtimeWindow.requestIdleCallback(doInitialLoad, { timeout: 3000 });
          // Start polling after a delay
          const interval = setInterval(() => {
            if (document.visibilityState === 'visible') {
              refreshBadgeCounts();
            }
          }, pollInterval);
          return () => {
            runtimeWindow.cancelIdleCallback?.(idleId);
            clearInterval(interval);
          };
        } else {
          // iOS fallback - use setTimeout with a small delay
          const timeoutId = setTimeout(doInitialLoad, 1000);
          const interval = setInterval(() => {
            if (document.visibilityState === 'visible') {
              refreshBadgeCounts();
            }
          }, pollInterval);
          return () => {
            clearTimeout(timeoutId);
            clearInterval(interval);
          };
        }
      } else {
        // Web/Desktop - load immediately
        doInitialLoad();
        const interval = setInterval(() => {
          if (document.visibilityState === 'visible') {
            refreshBadgeCounts();
          }
        }, pollInterval);
        return () => clearInterval(interval);
      }
    }
  }, [isAuthenticated, isSessionLocked, refreshBadgeCounts]);

  // Sync badge counts to desktop taskbar (only runs in Tauri desktop app)
  // This cycles between: normal icon → email badge → phone badge → chat badge → task badge
  useTaskbarBadge({
    unreadEmails: badgeCounts.emailUnreadCount,
    missedCalls: badgeCounts.missedCallsCount,
    unreadMessages: badgeCounts.messagesUnreadCount,
    tasksDueToday: badgeCounts.tasksDueTodayCount + badgeCounts.pendingMentionsCount + badgeCounts.pendingApprovalsCount,
    autoStart: true,
  });

  // Sync badge counts to mobile app icon (only runs in Capacitor Android/iOS app)
  const lastBadgeCount = useRef<number>(-1);
  useEffect(() => {
    const shouldSyncAppBadge = Capacitor.isNativePlatform() || isStandalonePwa();

    if (!shouldSyncAppBadge) {
      return;
    }
    
    const totalCount = badgeCounts.totalUnreadCount;
    
    // Only update if count actually changed
    if (totalCount === lastBadgeCount.current) {
      return;
    }
    
    lastBadgeCount.current = totalCount;
    
    // Update the app icon badge
    if (totalCount > 0) {
      AppBadge.setBadge({ count: totalCount }).catch((error: Error) => {
        console.debug('Failed to set app badge:', error.message);
      });
    } else {
      AppBadge.clearBadge().catch((error: Error) => {
        console.debug('Failed to clear app badge:', error.message);
      });
    }
  }, [badgeCounts.totalUnreadCount]);

  // Function to immediately update missed calls count (for real-time updates from call modal)
  const setMissedCallsCount = useCallback((count: number) => {
    setBadgeCounts(prev => ({
      ...prev,
      missedCallsCount: count,
      totalUnreadCount:
        prev.messagesUnreadCount
        + prev.emailUnreadCount
        + prev.tasksDueTodayCount
        + prev.pendingMentionsCount
        + prev.pendingApprovalsCount
        + count,
    }));
  }, []);

  const value = {
    badgeCounts,
    refreshBadgeCounts,
    setMissedCallsCount,
    loading,
  };

  return (
    <BadgeContext.Provider value={value}>
      {children}
    </BadgeContext.Provider>
  );
}

export function useBadge() {
  const context = useContext(BadgeContext);
  if (context === undefined) {
    throw new Error('useBadge must be used within a BadgeProvider');
  }
  return context;
}